/**
 * Telegram utility — единая точка для Telegram-ссылок виджета (#1493).
 *
 * По аналогии с `whatsapp.js`: все клики идут через openTelegram(), чтобы
 * трекинг и формат URL жили в одном месте.
 *
 * Админка хранит хендл как его ввёл дилер (`contact.telegram`): `andysmotors`,
 * `@andysmotors`, `t.me/andysmotors` или полный `https://t.me/andysmotors`.
 * Нормализация — здесь, а не в форме: так же работают и старые значения, и
 * `?contactTelegram=` / `data-contact-telegram`.
 */

import { trackTelegramClick } from '../services/analyticsBridge';
import { emitTelegramCarClick } from '../services/carEngagementEvents';

// Публичный username Telegram: 5–32 символа, латиница/цифры/подчёркивание.
const USERNAME_RE = /^[A-Za-z0-9_]{5,32}$/;
// Инвайт-ссылка в группу/канал: t.me/+<code>.
const INVITE_RE = /^\+[A-Za-z0-9_-]{8,}$/;

/**
 * Хендл дилера → `https://t.me/<name>`.
 *
 * @param {string|null|undefined} handle
 * @returns {string} URL или '' если хендл пустой/невалидный — тогда кнопку
 *   не показываем: мёртвый t.me хуже отсутствующей ссылки.
 */
export function telegramUrl(handle) {
  if (typeof handle !== 'string') return '';
  const path = handle
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^(www\.)?(t\.me|telegram\.me)\//i, '')
    .replace(/^@/, '');
  // Старая инвайт-ссылка `t.me/joinchat/<code>` — то же, что нынешняя
  // `t.me/+<code>`. Без этой ветки от неё оставался мёртвый t.me/joinchat.
  const legacyInvite = path.match(/^joinchat\/([^/?#]+)/i);
  const name = legacyInvite ? `+${legacyInvite[1]}` : path.split(/[/?#]/)[0];
  if (name.toLowerCase() === 'joinchat') return '';

  if (USERNAME_RE.test(name) || INVITE_RE.test(name)) {
    return `https://t.me/${name}`;
  }
  return '';
}

/**
 * Open Telegram chat with tracking.
 *
 * @param {Object} params
 * @param {string} params.handle - Telegram handle (any accepted format)
 * @param {string} [params.context] - Where the click happened (e.g. 'voice_widget')
 * @returns {boolean} true if opened, false if the handle was missing/invalid
 */
export function openTelegram({ handle, context }) {
  const url = telegramUrl(handle);
  if (!url) {
    console.error('[Telegram] Handle not provided or invalid');
    return false;
  }

  trackTelegramClick({ telegramUrl: url, context });
  // Per-car attribution for the Insights tab + lead interest score — fires only
  // when a car is on screen (no-op otherwise), same as WhatsApp.
  emitTelegramCarClick();
  window.open(url, '_blank', 'noopener');
  return true;
}
